import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router';
import { Menu, MenuItem } from '../ui/Menu';
import { itemCountLabel } from '../ui/counts';
import type { SortBinding } from '../ui/useDragSort';
import { useImageUrl } from '../db/useImageUrl';
import { countItems } from '../db/rooms';
import type { Room } from '../db/schema';
import { iconByName } from './roomIcons';
import { useRoomDialogs } from './RoomDialogs';

/**
 * Cover at 4:3, name in Fraunces beneath, item count in --stone. A room with
 * no cover shows a --sand field with its icon centred at 40%.
 *
 * The card is dragged to reorder, the same gesture as a tile on a board. The
 * menu used to carry Move Earlier / Move Later as well; they went once
 * dragging worked, being two ways to say one thing and the more confusing of
 * the two.
 */
export function RoomCard({
  room,
  ordinal,
  lifted,
  sort,
}: {
  room: Room;
  /** Its number among the rooms sharing its icon, when the icon repeats. */
  ordinal: number | undefined;
  /** Being dragged: off the page, following the cursor. */
  lifted: boolean;
  sort: SortBinding;
}) {
  const dialogs = useRoomDialogs();
  const url = useImageUrl(room.coverImageId);
  const count = useLiveQuery(() => countItems(room.id), [room.id]);
  const Icon = iconByName(room.icon);

  return (
    <li className={`room-card${lifted ? ' lifted' : ''}`} {...sort}>
      <Link
        to={`/room/${room.id}`}
        className="room-card__link"
        // An anchor is draggable by default, and the browser's own link drag
        // would fight this one for the gesture.
        draggable={false}
      >
        <div className="room-card__cover">
          {url === undefined ? (
            <span className="room-card__glyph">
              <Icon size={40} aria-hidden />
              {ordinal !== undefined && <span className="room-card__ordinal">{ordinal}</span>}
            </span>
          ) : (
            <>
              <img className="room-card__image" src={url} alt="" draggable={false} />
              {/* Only over a photo. A room with no cover already shows this
                  same glyph centred, and two of it on one card reads as a
                  mistake rather than as consistency. */}
              <span className="room-card__badge">
                <Icon size={16} aria-hidden />
                {ordinal !== undefined && <span className="room-card__ordinal">{ordinal}</span>}
              </span>
            </>
          )}
        </div>
        <div className="room-card__meta">
          <h2 className="display room-card__name">{room.name}</h2>
          <p className="room-card__count">{itemCountLabel(count)}</p>
        </div>
      </Link>

      <div className="room-card__menu">
        <Menu label={`Actions for ${room.name}`}>
          {(close) => (
            <>
              <MenuItem
                onSelect={() => {
                  close();
                  dialogs.openEdit(room);
                }}
              >
                Edit Room
              </MenuItem>
              <MenuItem
                onSelect={() => {
                  close();
                  dialogs.openDelete(room);
                }}
              >
                Delete Room
              </MenuItem>
            </>
          )}
        </Menu>
      </div>
    </li>
  );
}
